import type { MediaType } from "@/lib/types";
import { getGenresByType, regions, years } from "@/lib/catalog";

interface FilterSidebarProps {
  type: MediaType;
  genre: string;
  region: string;
  year: string;
  onSelect: (key: "genre" | "region" | "year", value: string) => void;
  onReset: () => void;
}

interface GroupProps {
  title: string;
  icon: string;
  options: { value: string; label: string }[];
  active: string;
  onSelect: (value: string) => void;
}

function FilterGroup({ title, icon, options, active, onSelect }: GroupProps) {
  return (
    <div className="border-b border-background-200 pb-4 last:border-0 last:pb-0">
      <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold text-foreground-900">
        <i className={`${icon} text-primary-500`}></i>
        {title}
      </h3>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onSelect(option.value)}
            className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
              active === option.value
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterSidebar({
  type,
  genre,
  region,
  year,
  onSelect,
  onReset,
}: FilterSidebarProps) {
  const genreOptions = [
    { value: "all", label: "全部" },
    ...getGenresByType(type).map((g) => ({ value: g.key, label: g.label })),
  ];

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-heading text-base font-extrabold text-foreground-950">
          <i className="ri-filter-3-line text-primary-500"></i>
          筛选条件
        </h2>
        <button
          type="button"
          onClick={onReset}
          className="cursor-pointer whitespace-nowrap text-xs font-semibold text-accent-600 transition-colors hover:text-accent-700"
        >
          重置
        </button>
      </div>
      <div className="flex flex-col gap-4">
        <FilterGroup
          title="类型"
          icon="ri-price-tag-3-line"
          options={genreOptions}
          active={genre}
          onSelect={(value) => onSelect("genre", value)}
        />
        <FilterGroup
          title="地区"
          icon="ri-earth-line"
          options={regions.map((r) => ({ value: r, label: r }))}
          active={region}
          onSelect={(value) => onSelect("region", value)}
        />
        <FilterGroup
          title="年份"
          icon="ri-calendar-line"
          options={years.map((y) => ({ value: y, label: y }))}
          active={year}
          onSelect={(value) => onSelect("year", value)}
        />
      </div>
    </div>
  );
}